import React from 'react';

import ZoneBadge from './ZoneBadge';
import RpeBar from './RpeBar';
import CoachNote from '../ui/CoachNote';
import IntensityBadge from '../ui/IntensityBadge';

// ── Formatting helpers ───────────────────────────────────────────────

function formatShort(iso) {
  if (!iso) return '';
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

function formatDuration(min) {
  if (!min || min <= 0) return '';
  if (min < 60) return `${min} דק'`;
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return `${h}:${String(m).padStart(2, '0')} שעות`;
}

function formatDistance(km, sportType) {
  if (km == null) return '';
  // 2026-06-14 — '0 ק"מ' on a pure strength day is meaningless; show
  // duration instead, or hide.
  if (km === 0) return '';
  return `${km} ק"מ`;
}

// Cycling pace ranges in the JSON often look like {min:'0:00', max:'0:00', label:'FTP 88-94%'}
// — the model uses the label for power info and the numbers are placeholders.
// Detect that and render only the label, never a fake "0:00 דק'/ק"מ".
function hasNumericPace(pace) {
  if (!pace) return false;
  const isPlaceholder = (v) => !v || v === '0:00' || v === '00:00';
  return !isPlaceholder(pace.min) && !isPlaceholder(pace.max);
}

// An interval set's two paces from its explicit steps: "מהיר: 4:19–4:29 · קל: 5:35–6:05".
// null when the segment is not such a set (every plan before 2026-10-19), so nothing changes there.
export function intervalSetPaces(seg) {
  const steps = Array.isArray(seg?.steps) ? seg.steps : null;
  if (!steps || steps.length === 0) return null;
  const work = steps.find(s => s && s.role === 'work' && hasNumericPace(s.pace));
  const rec = steps.find(s => s && s.role === 'recovery' && hasNumericPace(s.pace));
  if (!work) return null;
  const parts = [`מהיר: ${work.pace.min}–${work.pace.max}`];
  if (rec) parts.push(`קל: ${rec.pace.min}–${rec.pace.max}`);
  return parts.join(' · ');
}

function formatPace(pace, sportType) {
  if (!pace) return null;
  if (hasNumericPace(pace)) {
    const unit = sportType === 'cycling' ? 'דק\'/ק"מ' : 'דק\'/ק"מ';
    return { value: `${pace.min}–${pace.max} ${unit}`, label: pace.label };
  }
  // No numeric pace — for cycling this is normal (power info is in the label).
  if (pace.label) return { value: null, label: pace.label };
  return null;
}

// Map workout type name → IntensityBadge color. Decoupled from training-zone
// colors so we can paint the *workout label* (טמפו / אינטרוולים) in its own
// hue alongside the more granular ZoneBadge.
function colorForType(type) {
  if (!type) return 'cyan';
  const t = type.toLowerCase();
  if (t.includes('טמפו') || t.includes('tempo')) return 'purple';
  if (t.includes('אינטרוול') || t.includes('interval')) return 'rose';
  if (t.includes('ארוכ') || t.includes('long')) return 'teal';
  if (t.includes('קל') || t.includes('easy') || t.includes('recovery')) return 'emerald';
  if (t.includes('race') || t.includes('מרוץ')) return 'amber';
  if (t.includes('עליות') || t.includes('hill')) return 'orange';
  if (t.includes('sweet')) return 'pink';
  return 'cyan';
}

// ─────────────────────────────────────────────────────────────────────
// DayCard
//
// `day`     — one element of plan.days from WeeklyPlanV2
// `compact` — collapsed "road view"
// `sportType` — 'running' | 'cycling'
// ─────────────────────────────────────────────────────────────────────
export default function DayCard({ day, compact = false, sportType = 'running' }) {
  if (day.is_rest) return <RestDayCard day={day} compact={compact} />;
  if (compact) return <WorkoutDayCompact day={day} sportType={sportType} />;
  return <WorkoutDayFull day={day} sportType={sportType} />;
}

// ── Full view ────────────────────────────────────────────────────────

function WorkoutDayFull({ day, sportType }) {
  const pace = formatPace(day.pace, sportType);
  const isCycling = sportType === 'cycling';
  // Cycling prioritises duration over distance — the headline number
  // is the time, km appears small underneath as a hint. Running keeps
  // km as the headline (pace × distance is how runners think).
  // Pure strength (km=0) — show duration as headline, no km subline.
  const isPureStrength = !isCycling && (day.km === 0)
    && (day.type || '').includes('כוח');
  const headline = isCycling
    ? formatDuration(day.duration_min)
    : isPureStrength
      ? formatDuration(day.duration_min)
      : formatDistance(day.km, sportType);
  const subline = isCycling
    ? (day.km != null && day.km > 0 ? `~${day.km} ק"מ` : null)
    : isPureStrength
      ? null  // already showing duration as headline; no need for km
      : (day.duration_min ? formatDuration(day.duration_min) : null);
  return (
    <div className="rounded-2xl bg-surface border border-border p-4 hover:border-border transition">
      {/* Header line: day + date + icon + type-as-badge */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-text font-bold">{day.day_name}</span>
            <span className="text-text-muted text-sm">{formatShort(day.date)}</span>
            <span className="text-2xl">{day.icon || (isCycling ? '🚴' : '🏃')}</span>
            <IntensityBadge label={day.type} color={colorForType(day.type)} />
          </div>
        </div>
        <div className="shrink-0 text-left">
          <div className="text-text-body text-2xl font-bold leading-none">{headline || '—'}</div>
          {subline ? (
            <div className="text-text-muted text-xs mt-1">{subline}</div>
          ) : null}
        </div>
      </div>

      {/* Zone + RPE row — critical signals */}
      <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
        <ZoneBadge zone={day.target_zone} />
        <RpeBar rpe={day.rpe_effort} />
      </div>

      {/* Cycling-specific metrics row — FTP%, TSS, bike type.
          Only rendered when sport_type='cycling' and the orchestrator
          provided the values. Compact strip below the zone/RPE row. */}
      {sportType === 'cycling' && (day.ftp_pct || day.tss || day.bike_type) && (
        <div className="flex items-center gap-x-3 gap-y-1 flex-wrap mb-3 text-[12px]">
          {day.ftp_pct && (
            <span className="flex items-center gap-1">
              <span className="text-text-muted">⚡</span>
              <span className="text-text-body font-mono font-semibold tabular-nums">{day.ftp_pct}</span>
              <span className="text-text-muted">FTP</span>
            </span>
          )}
          {day.tss != null && day.tss > 0 && (
            <span className="flex items-center gap-1">
              <span className="text-text-muted">📊</span>
              <span className="text-text-body font-mono font-semibold tabular-nums">{day.tss}</span>
              <span className="text-text-muted">TSS</span>
            </span>
          )}
          {day.bike_type && (
            <span className="flex items-center gap-1">
              <span className="text-text-muted">🚲</span>
              <span className="text-text-body">
                {{ road: 'כביש', gravel: 'גראבל', mtb: 'הרים', trainer: 'טריינר' }[day.bike_type] || day.bike_type}
              </span>
            </span>
          )}
        </div>
      )}

      {/* Pace OR power (cycling uses pace.label for FTP info).
          Typography: numeric value is the primary read — 14px / 600.
          Label ("Zone 2 · קצב קל") is secondary — 13px / gray-400. */}
      {pace && (
        <div className="mb-2 flex flex-wrap items-baseline gap-x-2">
          <span className="text-text-muted text-sm">
            {sportType === 'cycling' ? 'הספק:' : 'קצב:'}
          </span>
          {pace.value && (
            <span className="font-mono font-semibold text-sm text-text tabular-nums">
              {pace.value}
            </span>
          )}
          {pace.label && (
            <span className="text-[13px] text-text-muted">
              {pace.value ? '· ' : ''}{pace.label}
            </span>
          )}
        </div>
      )}

      {/* Structure breakdown */}
      {day.structure?.length > 0 && (
        <div className="rounded-xl bg-bg border border-border p-3 mb-3">
          <div className="text-text-muted text-[11px] mb-2">מבנה האימון</div>
          <ul className="space-y-1.5">
            {day.structure.map((seg, i) => (
              <li key={i} className="flex items-center justify-between gap-3">
                {/* `display` is the athlete-facing prescription, written by the builder when the
                    accounting km is not what a coach would say out loud — "4 × 1600 מ' @ קצב סף,
                    90 שנ' ריצה קלה" rather than "7.15 ק"מ". It REPLACES the text+km pair, never
                    joins it, so the fake precision never reaches the screen. Absent (the default
                    on every plan generated before 2026-08-10) nothing changes. */}
                {/* An interval SET (2026-10-19) carries its explicit steps: ONE row for the whole
                    "N × (work + recovery)" cycle, its two paces on a second line — never a row per rep
                    and never a separate recovery row. */}
                {intervalSetPaces(seg) ? (
                  <span className="flex flex-col">
                    <span className="text-text text-sm">{seg.display || seg.segment}</span>
                    <span className="font-mono text-[13px] text-text-muted tabular-nums">
                      {intervalSetPaces(seg)}
                    </span>
                  </span>
                ) : (
                  <span className="text-text text-sm">{seg.display || seg.segment}</span>
                )}
                <span className="flex items-baseline gap-1.5 shrink-0">
                  {intervalSetPaces(seg) ? null : <>
                  {/* 2026-06-14 — hide '0 ק"מ' on strength segments;
                      show duration instead when available. */}
                  {seg.display ? null : seg.km > 0 ? (
                    <span className="font-mono font-semibold text-sm text-text tabular-nums">
                      {seg.km} ק"מ
                    </span>
                  ) : seg.duration_min ? (
                    <span className="font-mono font-semibold text-sm text-text tabular-nums">
                      {seg.duration_min} דק'
                    </span>
                  ) : null}
                  {seg.pace && hasNumericPace(seg.pace) && (
                    <span className="font-mono text-[13px] text-text-muted tabular-nums">
                      · {seg.pace.min}–{seg.pace.max}
                    </span>
                  )}
                  {seg.pace && !hasNumericPace(seg.pace) && seg.pace.label && (
                    <span className="text-[13px] text-text-muted">· {seg.pace.label}</span>
                  )}
                  </>}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Description */}
      {day.description && (
        <p className="text-text text-base leading-relaxed mb-3">{day.description}</p>
      )}

      {/* Coach notes — expandable/collapsible with preview */}
      <CoachNote
        className="mb-3"
        expandable
        preview={typeof day.coach_notes === 'string' ? day.coach_notes.slice(0, 60) : undefined}
      >
        {day.coach_notes}
      </CoachNote>

      {/* Tips list */}
      {day.tips?.length > 0 && (
        <ul className="space-y-1 mb-2">
          {day.tips.map((tip, i) => (
            <li key={i} className="text-text-muted text-xs flex gap-1.5">
              <span className="text-text-body shrink-0">›</span>
              <span>{tip}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Gear notes */}
      {day.gear_notes && (
        <div className="text-[11px] text-text-muted mt-2 flex gap-1.5">
          <span className="shrink-0">🎽</span>
          <span>{day.gear_notes}</span>
        </div>
      )}
    </div>
  );
}

// ── Compact ("road view") ────────────────────────────────────────────

function WorkoutDayCompact({ day, sportType }) {
  const pace = formatPace(day.pace, sportType);
  const isCycling = sportType === 'cycling';
  const headline = isCycling
    ? formatDuration(day.duration_min)
    : formatDistance(day.km, sportType);
  return (
    <div className="rounded-xl bg-surface border border-border px-3 py-2">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-text font-bold text-sm w-12 shrink-0">{day.day_name}</span>
        <span className="text-text-muted text-xs w-12 shrink-0">{formatShort(day.date)}</span>
        <span className="text-lg shrink-0">{day.icon || (isCycling ? '🚴' : '🏃')}</span>
        <span className="flex-1 truncate"><IntensityBadge label={day.type} color={colorForType(day.type)} size="sm" /></span>
        <span className="text-text-body font-bold text-sm shrink-0">{headline || '—'}</span>
      </div>
      <div className="flex items-center justify-between gap-2 text-xs">
        <ZoneBadge zone={day.target_zone} size="sm" />
        <RpeBar rpe={day.rpe_effort} size="sm" />
      </div>
      {pace && (
        <div className="text-[11px] text-text-muted mt-1 font-mono">
          {pace.value || ''}
          {pace.value && pace.label ? ' · ' : ''}
          {pace.label || ''}
        </div>
      )}
      <CoachNote compact className="mt-1">{day.coach_notes}</CoachNote>
    </div>
  );
}

// ── Rest day ─────────────────────────────────────────────────────────
// Visual treatment: half-transparent card so the eye reads it as a
// "calm gap" inside the week, not as a workout. Same skeleton as the
// workout card but stripped of pace / power / zone / RPE.

function RestDayCard({ day, compact }) {
  const isFull = day.rest_type === 'full';
  const label = isFull ? 'מנוחה מלאה' : 'מנוחה פעילה';
  const icon = isFull ? '😴' : '🚶';

  if (compact) {
    return (
      <div className="rounded-xl bg-pressed border border-border px-3 py-2 flex items-center gap-2 opacity-70">
        <span className="text-text text-sm font-bold w-12 shrink-0">{day.day_name}</span>
        <span className="text-text-muted text-xs w-12 shrink-0">{formatShort(day.date)}</span>
        <span className="text-lg shrink-0">{icon}</span>
        <span className="text-text-body text-sm flex-1">{label}</span>
      </div>
    );
  }

  return (
    <div className="rounded-2xl bg-pressed border border-border p-4 opacity-75 hover:opacity-100 transition">
      <div className="flex items-center gap-3 mb-2 min-w-0">
        <span className="text-2xl shrink-0">{icon}</span>
        <div className="min-w-0">
          <div className="text-text font-bold">{day.day_name} · <span className="text-text-muted font-normal">{formatShort(day.date)}</span></div>
          <div className="text-text-muted text-sm">{label}</div>
        </div>
      </div>
      {day.description && (
        <p className="text-text-body text-sm leading-relaxed">{day.description}</p>
      )}
      {day.gear_notes && (
        <div className="text-[11px] text-text-muted mt-2 flex gap-1.5">
          <span className="shrink-0">🎽</span>
          <span>{day.gear_notes}</span>
        </div>
      )}
    </div>
  );
}
